import React, { useState, useEffect } from "react";
import UserService from "services/user.service";
import { toast } from "react-toastify";

export default function ProfileEdit() {
  const [loading, setLoading] = useState(false);

  // États pour la visibilité des mots de passe
  const [showActual, setShowActual] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    login: "",
  });

  // Utilisation stricte de l'interface IChangePassword
  const [passwordData, setPasswordData] = useState({
    actualPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      // Utilise la méthode qui récupère l'utilisateur connecté (souvent /api/account dans JHipster)
      const res = await UserService.getCurrentAccount();
      setProfile(res.data);
    } catch (error) {
      toast.error("Erreur de chargement du profil");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation locale avant envoi
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return toast.error("Les nouveaux mots de passe ne correspondent pas");
    }

    if (passwordData.newPassword.length < 4) {
      return toast.error("Le nouveau mot de passe est trop court");
    }

    try {
      setLoading(true);
      // Appel de ta méthode : changePassword(data: IChangePassword)
      await UserService.changePassword(passwordData);

      toast.success("Mot de passe mis à jour avec succès");

      // Réinitialisation du formulaire de sécurité uniquement
      setPasswordData({
        actualPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error(error);
      toast.error(
        "Échec de la modification. Vérifiez votre mot de passe actuel.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Dans JHipster, saveAccount met à jour l'utilisateur courant (API /api/account)
      await UserService.changeCurrentUser(profile);
      toast.success("Profil mis à jour avec succès");

      loadProfile();

      // --- DÉCLENCHER LA MISE À JOUR DES INITIALES ---
      // On crée un événement personnalisé 'userProfileUpdated'
      const event = new CustomEvent("userProfileUpdated");
      window.dispatchEvent(event);
    } catch (error) {
      console.error("Erreur profil:", error);
      toast.error("Erreur lors de la mise à jour des informations");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen pt-28 md:pt-24 pb-12 mt-3 px-4 max-w-6xl mx-auto justify-start items-center">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 w-full">
        {/* --- SECTION INFOS PERSONNELLES --- */}
        <div className="lg:col-span-2">
          <div className="bg-[#0B1222] border border-white/10 rounded-[1.5rem] shadow-xl overflow-hidden">
            <div className="px-8 py-6 border-b border-white/5 bg-[#0F172A]/50">
              <h3 className="text-[#D4AF37] text-xs font-black uppercase tracking-[0.2em]">
                Informations du Compte
              </h3>
            </div>

            {/* Ajout du onSubmit ici */}
            <form onSubmit={handleProfileSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                    Prénom
                  </label>
                  <input
                    type="text"
                    required
                    value={profile.firstName || ""}
                    onChange={(e) =>
                      setProfile({ ...profile, firstName: e.target.value })
                    }
                    className="w-full bg-[#161F32]/50 border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:border-[#D4AF37] outline-none transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                    Nom
                  </label>
                  <input
                    type="text"
                    required
                    value={profile.lastName || ""}
                    onChange={(e) =>
                      setProfile({ ...profile, lastName: e.target.value })
                    }
                    className="w-full bg-[#161F32]/50 border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:border-[#D4AF37] outline-none transition-all"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                  Email / Identifiant (Lecture seule)
                </label>
                <input
                  type="email"
                  disabled
                  value={profile.email || ""}
                  className="w-full bg-[#161F32]/20 border border-white/5 rounded-xl px-4 py-3 text-sm text-slate-500 cursor-not-allowed"
                />
                <p className="text-[9px] text-slate-600 italic mt-1">
                  L'identifiant ne peut pas être modifié pour des raisons de
                  sécurité.
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0B1222] px-10 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-lg shadow-[#D4AF37]/10 disabled:opacity-50"
                >
                  {loading
                    ? "Enregistrement..."
                    : "Enregistrer les modifications"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* --- SECTION SÉCURITÉ (PASSWORD) --- */}
        <div className="lg:col-span-1">
          <div className="bg-[#0B1222] border border-white/10 rounded-[1.5rem] shadow-xl overflow-hidden h-full">
            <div className="px-8 py-6 border-b border-white/5 bg-[#0F172A]/50 flex items-center gap-2">
              <i className="fas fa-shield-alt text-[#D4AF37] text-xs"></i>
              <h3 className="text-white text-xs font-black uppercase tracking-[0.2em]">
                Sécurité
              </h3>
            </div>

            <form onSubmit={handlePasswordSubmit} className="p-8 space-y-5">
              {/* Mot de passe actuel */}
              <div className="space-y-2 relative">
                <label className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                  Ancien mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showActual ? "text" : "password"}
                    required
                    value={passwordData.actualPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        actualPassword: e.target.value,
                      })
                    }
                    className="w-full bg-[#161F32]/50 border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:border-[#D4AF37] outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowActual(!showActual)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600"
                  >
                    <i
                      className={`fas ${showActual ? "fa-eye-slash" : "fa-eye"} text-xs`}
                    ></i>
                  </button>
                </div>
              </div>

              {/* Nouveau mot de passe */}
              <div className="space-y-2 relative">
                <label className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showNew ? "text" : "password"}
                    required
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        newPassword: e.target.value,
                      })
                    }
                    className="w-full bg-[#161F32]/50 border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:border-[#D4AF37] outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600"
                  >
                    <i
                      className={`fas ${showNew ? "fa-eye-slash" : "fa-eye"} text-xs`}
                    ></i>
                  </button>
                </div>
              </div>

              {/* Confirmation */}
              <div className="space-y-2">
                <label className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                  Confirmation
                </label>
                <input
                  type={showNew ? "text" : "password"}
                  required
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className="w-full bg-[#161F32]/50 border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:border-[#D4AF37] outline-none transition-all"
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-transparent border border-[#D4AF37] text-[#D4AF37] py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#0B1222] transition-all disabled:opacity-50"
                >
                  {loading ? "Mise à jour..." : "Changer le mot de passe"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}